import { ArrowRight } from 'lucide-react';
import { HeroArchitectureDiagram } from '../visuals/HeroArchitectureDiagram';

// Animated background with flowing particles
const FlowingBackground = () => {
    return (
        <div className="absolute inset-0 overflow-hidden">
            {/* Mesh gradient overlay */}
            <div className="absolute inset-0 bg-mesh-gradient opacity-60" />

            {/* Animated gradient orbs */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-volt-500/10 rounded-full blur-3xl animate-pulse-slow" />
            <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-volt-500/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
            <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-volt-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

            {/* Grid pattern */}
            <div
                className="absolute inset-0 opacity-[0.02]"
                style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                     linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '60px 60px'
                }}
            />
        </div>
    );
};

export const LeadershipHero = () => {
    return (
        <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 min-h-screen flex items-center bg-obsidian-900 overflow-hidden">
            <FlowingBackground />

            {/* Gradient fade at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-obsidian-900 to-transparent" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                    {/* Left column: text (~55%) */}
                    <div className="lg:w-[55%]">
                        {/* Badge */}
                        <div className="mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-400 text-sm font-medium">
                                Kubernetes-Native &middot; Vendor-Agnostic
                            </span>
                        </div>

                        {/* Headline */}
                        <div className="mb-6 animate-fade-up" style={{ animationDelay: '0.2s' }}>
                            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight">
                                The Intelligence Layer
                                <br />
                                <span className="bg-gradient-to-r from-volt-400 to-volt-500 bg-clip-text text-transparent">
                                    for Distributed GPU Inference
                                </span>
                            </h1>
                        </div>

                        {/* One-liner */}
                        <div className="mb-10 animate-fade-up" style={{ animationDelay: '0.3s' }}>
                            <p className="text-lg md:text-xl text-gray-300 max-w-xl leading-relaxed">
                                Predict demand. Route to warm cache. Scale before the spike.
                                Agentic, real-time, batch — every workload, the right GPU.
                            </p>
                        </div>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row items-start gap-4 animate-fade-up" style={{ animationDelay: '0.4s' }}>
                            <a
                                href="/trial"
                                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-volt-500 text-obsidian-900 rounded-lg font-semibold text-lg hover:bg-volt-400 transition-all hover:shadow-volt group"
                            >
                                Request Access
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </a>
                            <a
                                href="#how-it-works"
                                className="inline-flex items-center gap-2 text-gray-400 hover:text-volt-400 font-medium transition-colors group py-4"
                            >
                                See How It Works
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>
                    </div>

                    {/* Right column: diagram (~45%) */}
                    <div className="lg:w-[45%] w-full flex items-center justify-center animate-fade-up" style={{ animationDelay: '0.5s' }}>
                        <HeroArchitectureDiagram />
                    </div>
                </div>
            </div>
        </section>
    );
};
